// Global
import { RichText, RichTextField } from '@sitecore-jss/sitecore-jss-nextjs';
import { RichTextProps } from '@sitecore-jss/sitecore-jss-nextjs/types/components/RichText';
import React, { useEffect, useState } from 'react';

// Local
import useIsEditing from 'lib/hooks/useIsEditing';
import { useFieldWithFallbacks } from 'lib/hooks/useFieldWithFallbacks';

interface RichTextWrapperProps extends RichTextProps, React.HTMLAttributes<HTMLDivElement> {
  fallbacks?: (RichTextField | undefined)[];
}

const RichTextWrapper = ({
  field,
  fallbacks,
  editable = true,
  className,
  ...props
}: RichTextWrapperProps): JSX.Element => {
  const isEditing = useIsEditing() && editable;

  const { renderField } = useFieldWithFallbacks(field, fallbacks);

  const updatedField = useUpdatedRichTextField({ field: renderField, editable });

  const hasValue = !!updatedField?.value;

  // We should only render if it has a value, or if we are editing
  const shouldRender = hasValue || isEditing;

  if (!shouldRender) return <></>;

  // In editing mode, move `className` to wrapping div,
  // because Sitecore doesn't render the props if field is empty,
  // and so the "rte" and other css classes are missing initially.
  if (isEditing) {
    return (
      <div className={`rte ${className ?? ''}`}>
        <RichText
          {...props}
          data-component="helpers/fieldwrappers/richtextwrapper"
          editable={editable}
          field={updatedField}
        />
      </div>
    );
  }
  return (
    <RichText
      {...props}
      className={`rte ${className ?? ''}`}
      data-component="helpers/fieldwrappers/richtextwrapper"
      editable={editable}
      field={updatedField}
    />
  );
};

const NEW_TAB_ICON_STRING = `<span class="svg-icon inline-flex align-middle -ml-2 h-6 w-6">
    <svg
      aria-hidden="true"
      class="inline ml-2 -mt-1 h-em w-em"
      fill="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.25 3.75H19.5a.75.75 0 01.75.75v11.25a.75.75 0 01-1.5 0V6.31L5.03 20.03a.75.75 0 01-1.06-1.06L17.69 5.25H8.25a.75.75 0 010-1.5z"
        clipRule="evenodd"
        fillRule="evenodd"
      ></path>
    </svg>
  </span>`;

function useUpdatedRichTextField({ field, editable = true }: RichTextProps) {
  const isEditing = useIsEditing() && editable;

  const [content, setContent] = useState(field?.value);

  const updatedField: RichTextField = {
    value: content || '',
  };

  // Run this client-side because we don't have access to the document server-side
  useEffect(() => {
    const template = document.createElement('template');
    template.innerHTML = field?.value || '';

    const links = [...template.content.querySelectorAll('a')];
    links.forEach((a) => {
      const href = a.getAttribute('href') || '';
      const isPdf = /\.pdf(\?|#|$)/i.test(href);
      const isExternal = href.startsWith('http') || a.getAttribute('target') === '_blank';

      if (isPdf) {
        // PDF link: add screen reader text and PDF label
        a.innerHTML = `${a.innerHTML}<span class="sr-only"> (Opens in a new tab)</span> <span>(PDF)</span>`;
      } else if (isExternal) {
        // External link: set to open in new tab, add screen reader text and icon
        a.setAttribute('target', '_blank');
        a.innerHTML = `${a.innerHTML}<span class="sr-only"> (Opens in a new tab)</span> ${NEW_TAB_ICON_STRING}`;
      }
    });

    // Update the content
    setContent(template.innerHTML);
  }, [field?.value]);

  return isEditing ? field : updatedField;
}

export default RichTextWrapper;
